"use client";

import { PRODUCT_TYPES } from "@/constants/productTypes";
import { useModalHelpers } from "@/contexts/ModalContext";
import { useLanguageAndCurrency } from "@/hooks/useLanguageAndCurrency";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { usePathname, useRouter } from "@/i18n/navigation";
import { commonService } from "@/services/commonService";
import { getRouteDetails } from "@/utils/routeHelper";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sendGTMEvent } from "@next/third-parties/google";
import clsx from "clsx";
import { useLocale } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

export default function SeachFormsCampaignListener({ wrapperClassName = "" }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const routeDetails = getRouteDetails(pathname);
  const t = useTranslationDetector();
  const { errorModal } = useModalHelpers();
  const locale = useLocale();
  const { selectedCurrency } = useLanguageAndCurrency();
  const {
    isAnyProductHome,
    isFlightHome,
    isBusTicketHome,
    isFerryTicketHome,
    isCarRentalHome,
    isHotelHome,
  } = routeDetails;

  if (!isAnyProductHome) return null;

  useEffect(() => {
    const campaignId = searchParams.get("campaignId");
    if (campaignId) {
      fetchCampaignData(campaignId);
    } else {
      setCampaign(null);
    }
  }, [searchParams]);

  const fetchCampaignData = async (campaignId) => {
    setLoading(true);
    try {
      const response = await commonService.getCampaignDataByCampaignId({
        CampaignId: campaignId,
        LanguageCode: locale.toUpperCase(),
        ProductType: isFlightHome
          ? PRODUCT_TYPES.FLIGHT.UPPER_CASE
          : isHotelHome
            ? PRODUCT_TYPES.HOTEL.UPPER_CASE
            : isFerryTicketHome
              ? PRODUCT_TYPES.FERRY.UPPER_CASE
              : isCarRentalHome
                ? PRODUCT_TYPES.CAR.UPPER_CASE
                : isBusTicketHome
                  ? PRODUCT_TYPES.BUS.UPPER_CASE
                  : null,
      });

      if (response.Campaign) {
        setCampaign(response.Campaign);
        sendGTMEvent({
          event: "custom_event",
          event_category: "coupon",
          event_action: "coupon_applied",
          coupon_name: response.Campaign?.CampaignCode || null,
          coupon_value: null,
          coupon_currency: selectedCurrency?.toUpperCase() || "TRY",
          coupon_category: response.Campaign.ProductType,
        });
      }
    } catch (err) {
      errorModal({
        message: t("error.couponCouldnApplied"),
        isBottomSheet: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const removeCampaign = (campaignId) => {
    setCampaign(null);

    const params = new URLSearchParams(searchParams.toString());
    params.delete("campaignId");

    const newQuery = params.toString();
    const newUrl = newQuery ? `${pathname}?${newQuery}` : pathname;

    router.push(newUrl);
  };
  return (
    campaign && (
      <span className={clsx(wrapperClassName)} dir="ltr">
        <div className="bg-promo-gradient border-border-promo-muted shadow-small inline-flex w-full items-stretch gap-2 rounded-lg border px-2 md:w-auto">
          <span className="flex items-center justify-center">
            {campaign?.ProductType === "Flight" ? (
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["airplane-up"]}
                className="text-promo !h-6 !w-6 self-center md:h-6 md:!w-6"
              />
            ) : campaign?.ProductType === "Hotel" ? (
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["new-hotel"]}
                className="text-promo !h-6 !w-6 self-center md:h-6 md:!w-6"
              />
            ) : campaign?.ProductType === "Ferry" ? (
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["new-ferry"]}
                className="text-promo !h-6 !w-6 self-center md:h-6 md:!w-6"
              />
            ) : campaign?.ProductType === "Car" ? (
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["new-car"]}
                className="text-promo !h-6 !w-6 self-center md:h-6 md:!w-6"
              />
            ) : campaign?.ProductType === "Bus" ? (
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["new-bus"]}
                className="text-promo !h-6 !w-6 self-center md:h-6 md:!w-6"
              />
            ) : null}
          </span>

          <div className="border-border-promo-muted relative border-l border-dashed">
            <span className="border-background-global absolute -top-[2px] -left-[10px] h-[10px] w-5 overflow-hidden border-t border-solid">
              <div className="bg-background-global border-border-promo-muted -mt-[9px] h-[18px] w-[18px] rounded-full border"></div>
            </span>
            <span className="border-background-global absolute -bottom-[1px] -left-[10px] h-[10px] w-5 overflow-hidden border-t border-solid">
              <div className="bg-background-global border-border-promo-muted h-[18px] w-[18px] rounded-full border"></div>
            </span>
          </div>

          <div className="flex w-full items-center justify-between gap-2 pr-2 md:w-auto">
            <div className="flex w-full flex-col gap-1 p-3 md:w-auto">
              <span className="inline-flex items-center justify-between gap-1 font-semibold md:text-lg">
                <span className="text-lg font-semibold">
                  {campaign?.DiscountText ||
                    campaign?.CampaignCode ||
                    campaign?.Title}
                </span>
                <span className="text-promo inline-flex items-center justify-center gap-1 md:hidden">
                  <div className="flex items-center justify-center p-[3px]">
                    <FontAwesomeIcon
                      className="!h-[18px] !w-[18px]"
                      icon={byPrefixAndName.fas["circle-check"]}
                    />
                  </div>
                  <span className="text-sm font-semibold">
                    {t("common.codeApplied")}
                  </span>
                </span>
              </span>
              <span className="text-sm">{campaign.TitleWithoutHtmlTags}</span>
            </div>
            <span className="text-promo hidden items-center justify-center gap-1 md:inline-flex">
              <div className="flex items-center justify-center p-[3px]">
                <FontAwesomeIcon
                  className="!h-[18px] !w-[18px]"
                  icon={byPrefixAndName.fas["circle-check"]}
                />
              </div>
              <span className="text-sm font-semibold">
                {t("common.codeApplied")}
              </span>
            </span>
          </div>
        </div>
      </span>
    )
  );
}
